// src/components/js/GenomeAssemblyComponent.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import '../css/GenomeAssemblyComponent.css';
import { API_BASE_URL } from "../../config"; 
import { FaArrowLeft } from 'react-icons/fa';

/* ------------------------------------------------------------------- */
const getRelatedLinks = (genome) => {
  const acc = genome?.assembly_accession;
  const name = genome?.assembly_name;

  let links = [];

  // 1. Special case: Salk_teff_dabbi_3.0
  if (name === "Salk_teff_dabbi_3.0") {
    links.push({
      label: "EnsemblPlants",
      url: "https://plants.ensembl.org/Eragrostis_tef/Info/Index"
    });
    links.push({
      label: "EMBL-EBI (ENA)",
      url: `https://www.ebi.ac.uk/ena/browser/view/${acc}`
    });
    links.push({
      label: "NCBI Assembly",
      url: `https://www.ncbi.nlm.nih.gov/assembly/${acc}`
    });
  }

  // 2. Special case: ASM97063v1
  if (acc === "ASM97063v1") {
    links.push({
      label: "NCBI Assembly",
      url: `https://www.ncbi.nlm.nih.gov/assembly/${acc}`
    });
    links.push({
      label: "NCBI Genome",
      url: `https://www.ncbi.nlm.nih.gov/datasets/genome/${acc}`
    });
  }

  // 3. Default fallback
  if (acc && !links.some(l => l.url.includes(acc))) {
    links.push({
      label: "NCBI Assembly",
      url: `https://www.ncbi.nlm.nih.gov/assembly/${acc}`
    });
    links.push({
      label: "EBI-ENA",
      url: `https://www.ebi.ac.uk/ena/browser/view/${acc}`
    });
  }

  return links;
};
/* ------------------------------------------------------------------- */

const ChromosomeTable = ({ assemblyAccession }) => {
  const [chromosomes, setChromosomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllChromosomes, setShowAllChromosomes] = useState(false);

  useEffect(() => {
    if (!assemblyAccession) return;
    setLoading(true);
    axios
      .get(`http://197.156.76.122/api/chromosomes/?genome_assembly=${assemblyAccession}`)
      .then((res) => {
        setChromosomes(res.data);
        setError(null);
      })
      .catch(() => {
        setError('❌ Failed to load chromosome data.');
        setChromosomes([]);
      })
      .finally(() => setLoading(false));
  }, [assemblyAccession]);

  const handleDownload = () => {
    const url = `http://197.156.76.122/api/chromosomes/report/${assemblyAccession}/`;
    axios
      .get(url)
      .then(() => {
        window.open(url, '_blank');
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          alert('❌ Chromosome report not available for this assembly.');
        } else {
          alert('❌ Failed to download report. Please try again.');
        }
      });
  };

  if (loading) return <p>Loading chromosome data...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!chromosomes.length) return <p>No chromosome or scaffold data available.</p>;

  const displayedChromosomes = showAllChromosomes ? chromosomes : chromosomes.slice(0, 10);

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div className="chromosome-header">
        <h4 style={{ display: 'inline-block' }}>Chromosome / Scaffold Metadata</h4>
       
      </div>
      <table className="chromosome-table" style={{ width: '100%', marginTop: '1rem' }}>
        <thead>
          <tr>
            <th>Sequence name</th>
            <th>Chromosome name</th>
            <th>Assembly accession</th>
            <th>GenBank accession</th>
            <th>Seq length</th>
            
          </tr>
        </thead>
        <tbody>
          {displayedChromosomes.map((chr) => (
            <tr key={chr.id}>
              <td>{chr.sequence_name || '—'}</td>
              <td>{chr.chromosome_name || '—'}</td>
              <td>{chr.assembly_accession || '—'}</td>
              <td>{chr.genbank_seq_accession || '—'}</td>
              <td>{chr.seq_length || '—'}</td>
              
            </tr>
          ))}
        </tbody>
      </table>
      {chromosomes.length > 10 && (
        <div style={{ marginTop: '0.5rem', textAlign: 'center' }}>
          <button className="toggle-btn" onClick={() => setShowAllChromosomes(!showAllChromosomes)}>
            {showAllChromosomes ? 'Show Less' : `Show More (${chromosomes.length - 10} more)`}
          </button>
        </div>
      )}
    </div>
  );
};

const GenomeAssemblyComponent = () => {
  const navigate = useNavigate();
  const [genomes, setGenomes] = useState([]);
  const [allGenomes, setAllGenomes] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ftpVisibility, setFtpVisibility] = useState({});

  useEffect(() => {
  const fetchData = async () => {
    try {
      const res = await axios.get(
        `http://197.156.76.122/api/genomeassembly/?organism=Eragrostis%20tef`
      );

      const results = Array.isArray(res.data?.results)
        ? res.data.results
        : res.data;

      setAllGenomes(results);
      setGenomes(results.slice(0, 5));
      setError(null);
    } catch (err) {
      setError('❌ Failed to fetch genome assemblies.');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, []);


  const toggleExpand = (genome) => {
    const id = genome?.id || genome?.assembly_accession;
    setExpanded(expanded === id ? null : id);
  };

  const goToBioProject = (accession, e) => {
    e.stopPropagation(); // Prevent collapsing the card
    if (accession) {
      navigate(`/BioProject/${accession}`);
    }
  };

  return (
    <div className="genome-wrapper">
      
	  {/* 🔙 Back Button */}
	  <button 
	    className="back-button"
	    onClick={() => navigate('/')}
	  >
	    <FaArrowLeft /> Back to Home
	  </button>

      <motion.h2 className="genome-heading">
        Genome Assemblies of <em>Eragrostis tef</em>
      </motion.h2>

      {loading ? (
        <div className="loader">⏳ Loading genomes...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="genome-grid">
          {genomes.map((genome) => {
            const id = genome?.id || genome?.assembly_accession;
            const rawFtp = genome?.ftp_path ?? '';
            const ftpLinks = rawFtp.split('\n').map((l) => l.trim()).filter((l) => l.startsWith('http'));
            const relatedLinks = getRelatedLinks(genome);

            return (
              <motion.div key={id} className={`genome-card ${expanded === id ? 'expanded' : ''}`}>

                <div className="genome-header" onClick={() => toggleExpand(genome)}>
                  <div className="genome-info">
                    <h3>
                      {genome?.assembly_name}{' '}
                      <span className="organism-name">– {genome?.organism_scientific_name}</span>
                    </h3>
                  </div>
                  <div className="expand-icon">{expanded === id ? <ChevronUp /> : <ChevronDown />}</div>
                </div>

                <AnimatePresence>
                  {expanded === id && (
                    <motion.div className="genome-details">

                      <div className="details-grid">
                        <p><strong>Assembly Name:</strong> {genome?.assembly_name}</p>
                        <p><strong>Assembly Accession:</strong> {genome?.assembly_accession}</p>
                        <p><strong>Assembly Type:</strong> {genome?.assembly_type}</p>
                        <p><strong>Assembly Level:</strong> {genome?.assembly_level}</p>
                        <p><strong>Assembly Status:</strong> {genome?.assembly_status}</p>
                        <p><strong>RefSeq Category:</strong> {genome?.refseq_category}</p>
                        <p><strong>Submitter:</strong> {genome?.submitter}</p>
                        <p><strong>Submission Date:</strong> {genome?.submission_date}</p>
                        <p><strong>Organism Common Name:</strong> {genome?.organism_common_name}</p>

                        {/* 🔗 Clickable BioSample */}
                        <p>
                          <strong>BioSample:</strong>{' '}
                          {genome?.biosample_id ? (
                            <span
                              style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/BioSample/${genome.biosample_id}`);
                              }}
                            >
                              {genome.biosample_id}
                            </span>
                          ) : '—'}
                        </p>

                        {/* 🔗 Clickable BioProject */}
                        <p>
                          <strong>BioProject:</strong>{' '}
                          {genome?.bioproject_accession ? (
                            <span
                              style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
                              onClick={(e) => goToBioProject(genome.bioproject_accession, e)}
                            >
                              {genome.bioproject_accession}
                            </span>
                          ) : '—'}
                        </p>

                        <p><strong>WGS Project:</strong> {genome?.wgs_project_accession}</p>
                        <p><strong>Sequencing Technology:</strong> {genome?.sequencing_technology}</p>
                        <p><strong>Assembly Method:</strong> {genome?.assembly_method}</p>
                      </div>

                      {relatedLinks.length > 0 && (
                        <div className="related-links-box" style={{ marginTop: '1rem' }}>
                          <h4>🔗 Related Genome Links</h4>
                          <ul>
                            {relatedLinks.map((link, idx) => (
                              <li key={idx}>
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {link.label}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {ftpLinks.length > 0 && (
                        <div className="ftp-links-box">
                          <h4
                            className="ftp-title"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFtpVisibility((prev) => ({ ...prev, [id]: !prev[id] }));
                            }}
                          >
                            FTP {ftpVisibility[id] ? '▲' : '▼'}
                          </h4>

                          {ftpVisibility[id] && (
                            <ul className="ftp-list">
                              {ftpLinks.map((link, idx) => (
                                <li key={idx}>
                                  <a
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {link.split('/').pop()}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      <ChromosomeTable assemblyAccession={genome.assembly_accession} />

                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GenomeAssemblyComponent;

